import { useState } from "react";
import { SiTelegram, SiWhatsapp } from "react-icons/si";
import { MessageCircle, X } from "lucide-react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { useLanguage } from "../contexts/LanguageContext";
import { sendFunnelEvent } from "../lib/funnelEvents";
import { track as trackMetaPixelEvent } from "../lib/metaPixel";
import { trackContactClick } from "../lib/ga4";

const channels = [
    {
        id: "whatsapp",
        Icon: SiWhatsapp,
        hrefKey: "contact_whatsapp",
        label: "WhatsApp",
        sub: "Dakikalar içinde yanıt",
        bg: "bg-[#25D366]",
        ring: "shadow-[0_0_24px_rgba(37,211,102,0.45)]",
    },
    {
        id: "telegram",
        Icon: SiTelegram,
        hrefKey: "contact_telegram",
        label: "Telegram",
        sub: "Doğrudan destek",
        bg: "bg-[#229ED9]",
        ring: "shadow-[0_0_24px_rgba(34,158,217,0.45)]",
    },
];

export default function FloatingContact() {
    const [open, setOpen] = useState(false);
    const { language } = useLanguage();
    const { settings } = useSiteSettings();
    const visible = channels.filter((c) => settings[c.hrefKey]);
    const subs = {
        en: { whatsapp: "Reply within minutes", telegram: "Direct support", open: "Open contact menu", close: "Close contact menu" },
        pt: { whatsapp: "Resposta em minutos", telegram: "Suporte direto", open: "Abrir menu de contato", close: "Fechar menu de contato" },
        es: { whatsapp: "Respuesta en minutos", telegram: "Soporte directo", open: "Abrir menú de contacto", close: "Cerrar menú de contacto" },
        ru: { whatsapp: "Ответ за минуты", telegram: "Прямая поддержка", open: "Открыть меню контактов", close: "Закрыть меню контактов" },
        tr: { whatsapp: "Dakikalar içinde yanıt", telegram: "Doğrudan destek", open: "İletişim menüsünü aç", close: "İletişim menüsünü kapat" },
    }[language] || { whatsapp: "Reply within minutes", telegram: "Direct support", open: "Open contact menu", close: "Close contact menu" };
    if (visible.length === 0) return null;

    const handleContactClick = (channel) => {
        sendFunnelEvent(channel === "telegram" ? "contact_telegram" : "contact_whatsapp", {
            content_name: `floating_contact_${channel}`,
            content_category: "contact_click",
        });
        trackMetaPixelEvent("Contact", { channel, placement: "floating" });
        trackContactClick(channel, { placement: "floating" });
    };

    return (
        <div
            data-testid="floating-contact"
            className="fixed z-[60] bottom-6 right-6 flex flex-col items-end gap-3"
        >
            {open && (
                <div className="space-y-3 animate-fade-in">
                    {visible.map((c) => (
                        <a
                            key={c.id}
                            href={settings[c.hrefKey] || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleContactClick(c.id)}
                            data-testid={`floating-${c.id}-link`}
                            className="flex items-center gap-3 bg-card border border-white/10 hover:border-white/20 rounded-full pl-4 pr-1.5 py-1.5 transition-all"
                        >
                            <div className="text-right">
                                <p className="text-xs font-semibold leading-tight">
                                    {c.label}
                                </p>
                                <p className="text-[10px] font-mono text-muted-foreground tracking-wide">
                                    {c.id === "whatsapp" ? subs.whatsapp : subs.telegram}
                                </p>
                            </div>
                            <span
                                className={`grid place-items-center w-10 h-10 rounded-full text-white ${c.bg} ${c.ring}`}
                            >
                                <c.Icon className="w-5 h-5" />
                            </span>
                        </a>
                    ))}
                </div>
            )}

            <button
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? subs.close : subs.open}
                data-testid="floating-contact-toggle"
                className={`grid place-items-center w-14 h-14 rounded-full text-primary-foreground bg-primary hover:bg-primary/90 shadow-[0_0_30px_rgba(0, 0, 0,0.5)] transition-all ${
                    open ? "rotate-90" : ""
                }`}
            >
                {open ? (
                    <X className="w-6 h-6" />
                ) : (
                    <MessageCircle className="w-6 h-6" />
                )}
            </button>
        </div>
    );
}
